import { ArrowsOppositeToDots, Folder, Person, TrashBin } from '@gravity-ui/icons';
import { useNavigate } from 'react-router-dom';
import type { ProjectMergeSummary } from '@aioe/shared';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { OwnerPicker } from './OwnerPicker.tsx';
import { Button, ChoiceSelect, ConfirmDialog, ErrorNote, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';
import { timeAgo, useLoad } from '../hooks.ts';

const STATE_MARK = { active: 'online', stale: 'idle', archived: 'disabled', merged: 'disabled' } as const;
const STATE_TEXT = { active: 'Active', stale: 'Stale', archived: 'Archived', merged: 'Merged' } as const;
const STATE_BODY = {
  active: 'In use. It turns stale after a month in which no machine reports one of its repositories.',
  stale: 'No machine has reported one of its repositories for a month.',
  archived: 'Read-only: nothing can be added, and it is out of the pickers and the workbench\'s list until an owner restores it.',
  merged: 'This project was merged into another.',
} as const;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Where a project is in its life, and what its owners can do about it: archive, hand over, merge, remove. */
export function ProjectLifecycle({ projectId, name, state, archivedAt, admin, onChange, onRemoved }: { projectId: string; name: string; state: keyof typeof STATE_TEXT; archivedAt?: string; admin: boolean; onChange: () => void; onRemoved: () => void }) {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<'archive' | 'restore' | 'remove' | 'owner' | 'merge'>();
  const [owner, setOwner] = useState<{ id: string; name: string }>();
  const [into, setInto] = useState<string>();
  const [counts, setCounts] = useState<ProjectMergeSummary>();
  const [error, setError] = useState<string>();
  const [note, setNote] = useState<string>();
  const others = useLoad(() => routes.projects().then((p) => p.filter((x) => x.id !== projectId)), [projectId]);
  const options = Object.fromEntries((others.data ?? []).map((p) => [p.id, p.name])) as Record<string, string>;
  const run = async (f: () => Promise<unknown>, after: () => void) => {
    setError(undefined);
    try {
      await f();
      setDialog(undefined);
      after();
    } catch (e) {
      setDialog(undefined);
      setError((e as Error).message);
    }
  };
  const pickTarget = (id: string) => {
    setInto(id);
    setCounts(undefined);
    setError(undefined);
    void routes.mergeDryRun(projectId, id).then((r) => {
      setCounts(r.counts);
      setDialog('merge');
    }).catch((e) => setError((e as Error).message));
  };
  const target = into ? options[into] : undefined;
  return (
    <>
      <ListGroup title="Lifecycle" description="Archive it when the work is over, hand it to someone else, or fold it into another project.">
        <ListRow
          icon={<Folder />}
          title="State"
          description={`${STATE_BODY[state]}${state === 'archived' && archivedAt ? ` Archived ${timeAgo(archivedAt)}.` : ''}`}
          status={<StatusMark state={STATE_MARK[state]} label={STATE_TEXT[state]} />}
          control={
            state === 'archived' ? (
              <Button size="sm" variant="secondary" onPress={() => setDialog('restore')}>
                Restore
              </Button>
            ) : (
              <Button size="sm" variant="secondary" onPress={() => setDialog('archive')}>
                Archive
              </Button>
            )
          }
        />
        {state !== 'archived' ? (
          <ListRow
            icon={<Person />}
            title="Make someone the owner"
            description="Choose someone on the project. You stay on it as a contributor. To hand it to someone new, add them to the project first."
            control={
              <OwnerPicker
                projectId={projectId}
                label="New owner"
                noneLabel="Choose a person"
                className="w-full sm:w-56"
                exclude={(p) => p.role === 'owner'}
                value={dialog === 'owner' ? owner?.id : undefined}
                onChange={(o) => {
                  if (!o?.id) return;
                  setOwner({ id: o.id, name: o.name });
                  setDialog('owner');
                }}
              />
            }
          />
        ) : null}
        {state !== 'archived' ? (
          <ListRow
            icon={<ArrowsOppositeToDots />}
            title="Merge into another project"
            description="For two projects on the same repository. You see what moves before anything is asked."
            control={<ChoiceSelect<string> label="Project to merge into" value={into} options={options} placeholder="Choose a project" onChange={pickTarget} />}
          />
        ) : null}
        <ListRow
          icon={<TrashBin />}
          title="Remove the project"
          description="Its boards, memory and records on the organisation go with it."
          compactControl
          control={
            <Button size="sm" variant="danger-soft" onPress={() => setDialog('remove')}>
              Remove
            </Button>
          }
        />
        {note ? <ListBlock className="text-sm text-muted">{note}</ListBlock> : null}
        {error ? (
          <ListBlock>
            <ErrorNote error={error} />
          </ListBlock>
        ) : null}
      </ListGroup>
      <ConfirmDialog isOpen={dialog === 'archive'} title={`Archive ${name}`} confirmLabel="Archive" onCancel={() => setDialog(undefined)} onConfirm={() => run(() => routes.archiveProject(projectId), onChange)}>
        It becomes read-only and leaves the project lists and every workbench's picker. Nothing is deleted, and an owner can restore it at any time.
      </ConfirmDialog>
      <ConfirmDialog isOpen={dialog === 'restore'} title={`Restore ${name}`} confirmLabel="Restore" onCancel={() => setDialog(undefined)} onConfirm={() => run(() => routes.restoreProject(projectId), onChange)}>
        It becomes active again and its members' workbenches can register it.
      </ConfirmDialog>
      <ConfirmDialog isOpen={dialog === 'owner'} title={`Make ${owner?.name ?? 'this person'} the owner`} confirmLabel="Make owner" onCancel={() => setDialog(undefined)} onConfirm={() => owner && run(() => routes.transferProject(projectId, owner.id), onChange)}>
        {owner?.name} becomes the owner of {name}. You stay on it as a contributor. The change is in the project's audit trail.
      </ConfirmDialog>
      <ConfirmDialog
        isOpen={dialog === 'merge'}
        title={`Merge ${name} into ${target ?? 'the other project'}`}
        confirmLabel={admin ? 'Merge' : 'Ask to merge'}
        onCancel={() => setDialog(undefined)}
        onConfirm={() => into && run(() => routes.mergeProject(projectId, into, admin).then((r) => { if (r.merged) navigate(`/projects/${encodeURIComponent(into)}`); else setNote(`Asked the owners of ${target}; it merges when one of them approves.`); }), onChange)}
      >
        {counts && (
          <p>
            {plural(counts.roots, 'repository', 'repositories')}, {plural(counts.members, 'member')}, {plural(counts.tasks, 'task')} and {plural(counts.records, 'record')} move{counts.renumbered ? `; ${plural(counts.renumbered, 'task')} get a new number because ${target} already uses theirs` : ''}.
          </p>
        )}
        <p>{admin ? 'As an administrator you merge at once, without waiting for the owners of ' + target + '.' : `The owners of ${target} are asked first; the project merges when one approves. If you own both it merges at once.`} Everyone on {name} joins {target}, each at the higher of their two roles. {name} stays as a pointer, so a workbench that still has its id finds the new project.</p>
      </ConfirmDialog>
      <ConfirmDialog isOpen={dialog === 'remove'} title={`Remove the project ${name}`} confirmLabel="Remove" danger onCancel={() => setDialog(undefined)} onConfirm={() => run(() => routes.removeProject(projectId), onRemoved)}>
        <p>Its boards, memory and records on the organisation go with it. A project that holds work must be archived first, and the organisation export keeps a copy of everything, so export first if you may want it back.</p>
        <p>A workbench that still carries the project id registers it again on its next start.</p>
      </ConfirmDialog>
    </>
  );
}
